import React from 'react';
import { Modal, Table, Tag } from 'antd';

export interface CycleLogConfirmModalProps {
  visible: boolean;
  onConfirm: (values: any) => void;
  onCancel: () => void;
  loading: boolean;
  pendingValues: any;
  allItems: any[];
  storehouses: any[];
  cycle: any;
  isMobile?: boolean;
}

export const CycleLogConfirmModal: React.FC<CycleLogConfirmModalProps> = ({
  visible,
  onConfirm,
  onCancel,
  loading,
  pendingValues,
  allItems,
  storehouses,
  cycle,
  isMobile = false,
}) => {
  return (
    <Modal
      title="Confirmar Registro de Evento"
      open={visible}
      onOk={() => onConfirm(pendingValues)}
      onCancel={onCancel}
      confirmLoading={loading}
      maskClosable={false}
      okText="Registrar y Sincronizar"
      cancelText="Revisar"
      width={isMobile ? '100%' : 700}
      style={
        isMobile
          ? { top: 10, maxWidth: '100vw', padding: '0 4px' }
          : undefined
      }
    >
      <p style={{ marginBottom: 12 }}>
        ¿Está seguro de que los datos son correctos? Al guardar este evento:
      </p>

      {pendingValues?.log_type === 'INPUT_APPLICATION' && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8, fontSize: 13 }}>
            <strong>Almacén de Origen: </strong>
            <Tag color="blue">
              {storehouses.find((s: any) => s.id === pendingValues?.storehouse_id)
                ?.name ||
                storehouses.find(
                  (s: any) => s.id === pendingValues?.storehouse_id
                )?.alias ||
                'Bodega Principal'}
            </Tag>
          </div>
          <Table
            size="small"
            pagination={false}
            dataSource={pendingValues?.inputs || []}
            rowKey={(_, idx) => String(idx)}
            scroll={{ x: 'max-content' }}
            columns={[
              {
                title: 'Insumo / Servicio',
                key: 'item',
                render: (_, record: any) => {
                  const item = allItems.find(
                    (i: any) => i.id === record.element_id
                  );
                  const isServ = item?.register_type === 'SERVICE';
                  const code = item?.custom_id || item?.sku;
                  return (
                    <div>
                      <strong>{item?.name || 'Insumo'}</strong>
                      <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                        <Tag
                          color={isServ ? 'geekblue' : 'green'}
                          style={{
                            fontSize: 9,
                            lineHeight: '14px',
                            padding: '0 3px',
                            margin: 0,
                          }}
                        >
                          {isServ ? 'Servicio' : 'Insumo'}
                        </Tag>
                        {code && (
                          <span style={{ marginLeft: 4 }}>Ref: {code}</span>
                        )}
                      </div>
                    </div>
                  );
                },
              },
              {
                title: 'Cantidad',
                key: 'quantity',
                align: 'right',
                render: (_, record: any) => {
                  const item = allItems.find(
                    (i: any) => i.id === record.element_id
                  );
                  return (
                    <span>
                      {record.quantity} {item?.measure_unit || ''}
                    </span>
                  );
                },
              },
              {
                title: 'Costo U.',
                dataIndex: 'unit_price',
                key: 'unit_price',
                align: 'right',
                render: (val) =>
                  `$ ${Number(val || 0).toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`,
              },
              {
                title: 'Total',
                key: 'total',
                align: 'right',
                render: (_, record: any) => {
                  const tot =
                    (Number(record.quantity) || 0) *
                    (Number(record.unit_price) || 0);
                  return (
                    <strong>
                      ${' '}
                      {tot.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </strong>
                  );
                },
              },
            ]}
            summary={() => (
              <Table.Summary.Row style={{ background: '#fafafa' }}>
                <Table.Summary.Cell index={0} colSpan={3}>
                  <strong>Total Evento Insumos:</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <strong style={{ color: '#1890ff' }}>
                    ${' '}
                    {(pendingValues?.inputs || [])
                      .reduce(
                        (sum: number, r: any) =>
                          sum +
                          (Number(r.quantity) || 0) *
                            (Number(r.unit_price) || 0),
                        0
                      )
                      .toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                  </strong>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            )}
          />
          <div style={{ marginTop: 8, fontSize: 12, color: '#ff4d4f' }}>
            * Se <b>descontará</b> el stock de los productos físicos del almacén
            seleccionado.
          </div>
        </div>
      )}

      {pendingValues?.log_type === 'SUPPLY_PURCHASE' && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8, fontSize: 13 }}>
            <strong>Almacén de Destino (Recepción): </strong>
            <Tag color="orange">
              {storehouses.find((s: any) => s.id === pendingValues?.storehouse_id)
                ?.name ||
                storehouses.find(
                  (s: any) => s.id === pendingValues?.storehouse_id
                )?.alias ||
                'Bodega Principal'}
            </Tag>
          </div>
          <Table
            size="small"
            pagination={false}
            dataSource={pendingValues?.inputs || []}
            rowKey={(_, idx) => String(idx)}
            scroll={{ x: 'max-content' }}
            columns={[
              {
                title: 'Insumo Comprado',
                key: 'item',
                render: (_, record: any) => {
                  const item = allItems.find(
                    (i: any) => i.id === record.element_id
                  );
                  const isServ = item?.register_type === 'SERVICE';
                  const code = item?.custom_id || item?.sku;
                  return (
                    <div>
                      <strong>{item?.name || 'Insumo'}</strong>
                      <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                        <Tag
                          color={isServ ? 'geekblue' : 'green'}
                          style={{
                            fontSize: 9,
                            lineHeight: '14px',
                            padding: '0 3px',
                            margin: 0,
                          }}
                        >
                          {isServ ? 'Servicio' : 'Insumo'}
                        </Tag>
                        {code && (
                          <span style={{ marginLeft: 4 }}>Ref: {code}</span>
                        )}
                      </div>
                    </div>
                  );
                },
              },
              {
                title: 'Cantidad',
                key: 'quantity',
                align: 'right',
                render: (_, record: any) => {
                  const item = allItems.find(
                    (i: any) => i.id === record.element_id
                  );
                  return (
                    <span>
                      {record.quantity} {item?.measure_unit || ''}
                    </span>
                  );
                },
              },
              {
                title: 'Precio Compra U.',
                dataIndex: 'unit_price',
                key: 'unit_price',
                align: 'right',
                render: (val) =>
                  `$ ${Number(val || 0).toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`,
              },
              {
                title: 'Total',
                key: 'total',
                align: 'right',
                render: (_, record: any) => {
                  const tot =
                    (Number(record.quantity) || 0) *
                    (Number(record.unit_price) || 0);
                  return (
                    <strong style={{ color: '#fa8c16' }}>
                      ${' '}
                      {tot.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </strong>
                  );
                },
              },
            ]}
            summary={() => (
              <Table.Summary.Row style={{ background: '#fafafa' }}>
                <Table.Summary.Cell index={0} colSpan={3}>
                  <strong>Total Compra de Insumos:</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <strong style={{ color: '#fa8c16' }}>
                    ${' '}
                    {(pendingValues?.inputs || [])
                      .reduce(
                        (sum: number, r: any) =>
                          sum +
                          (Number(r.quantity) || 0) *
                            (Number(r.unit_price) || 0),
                        0
                      )
                      .toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                  </strong>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            )}
          />
          <div style={{ marginTop: 8, fontSize: 12, color: '#52c41a' }}>
            * Se <b>incrementará</b> el stock de los productos físicos en el
            almacén de destino (Comprobante de Compra).
          </div>
        </div>
      )}

      {pendingValues?.log_type === 'LABOR_LOG' && (
        <div style={{ marginBottom: 16 }}>
          {pendingValues?.contact_name && (
            <div style={{ marginBottom: 8, fontSize: 13 }}>
              <strong>Trabajador / Jornalero: </strong>
              <Tag color="purple">{pendingValues.contact_name}</Tag>
            </div>
          )}
          <Table
            size="small"
            pagination={false}
            dataSource={pendingValues?.inputs || []}
            rowKey={(_, idx) => String(idx)}
            scroll={{ x: 'max-content' }}
            columns={[
              {
                title: 'Labor / Actividad',
                key: 'item',
                render: (_, record: any) => {
                  const item = allItems.find(
                    (i: any) => i.id === record.element_id
                  );
                  return <strong>{item?.name || 'Labor'}</strong>;
                },
              },
              {
                title: 'Jornales / Cant.',
                key: 'quantity',
                align: 'right',
                render: (_, record: any) => <span>{record.quantity}</span>,
              },
              {
                title: 'Tarifa U.',
                dataIndex: 'unit_price',
                key: 'unit_price',
                align: 'right',
                render: (val) =>
                  `$ ${Number(val || 0).toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`,
              },
              {
                title: 'Total',
                key: 'total',
                align: 'right',
                render: (_, record: any) => {
                  const tot =
                    (Number(record.quantity) || 0) *
                    (Number(record.unit_price) || 0);
                  return (
                    <strong>
                      ${' '}
                      {tot.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </strong>
                  );
                },
              },
            ]}
            summary={() => (
              <Table.Summary.Row style={{ background: '#fafafa' }}>
                <Table.Summary.Cell index={0} colSpan={3}>
                  <strong>Total Mano de Obra:</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <strong style={{ color: '#722ed1' }}>
                    ${' '}
                    {(pendingValues?.inputs || [])
                      .reduce(
                        (sum: number, r: any) =>
                          sum +
                          (Number(r.quantity) || 0) *
                            (Number(r.unit_price) || 0),
                        0
                      )
                      .toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                  </strong>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            )}
          />
          <div style={{ marginTop: 8, fontSize: 12, color: '#722ed1' }}>
            * Se registrará el costo de mano de obra en el ciclo y se generará el
            comprobante de egreso automáticamente.
          </div>
        </div>
      )}

      {pendingValues?.log_type === 'SERVICE_EXPENSE' && (
        <div style={{ marginBottom: 16 }}>
          {pendingValues?.contact_name && (
            <div style={{ marginBottom: 8, fontSize: 13 }}>
              <strong>Proveedor / Transportador: </strong>
              <Tag color="cyan">{pendingValues.contact_name}</Tag>
            </div>
          )}
          <Table
            size="small"
            pagination={false}
            dataSource={pendingValues?.inputs || []}
            rowKey={(_, idx) => String(idx)}
            scroll={{ x: 'max-content' }}
            columns={[
              {
                title: 'Servicio / Concepto',
                key: 'item',
                render: (_, record: any) => {
                  const item = allItems.find(
                    (i: any) => i.id === record.element_id
                  );
                  return <strong>{item?.name || 'Servicio'}</strong>;
                },
              },
              {
                title: 'Cantidad',
                key: 'quantity',
                align: 'right',
                render: (_, record: any) => {
                  const item = allItems.find(
                    (i: any) => i.id === record.element_id
                  );
                  return (
                    <span>
                      {record.quantity} {item?.measure_unit || ''}
                    </span>
                  );
                },
              },
              {
                title: 'Valor U.',
                dataIndex: 'unit_price',
                key: 'unit_price',
                align: 'right',
                render: (val) =>
                  `$ ${Number(val || 0).toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`,
              },
              {
                title: 'Total',
                key: 'total',
                align: 'right',
                render: (_, record: any) => {
                  const tot =
                    (Number(record.quantity) || 0) *
                    (Number(record.unit_price) || 0);
                  return (
                    <strong>
                      ${' '}
                      {tot.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </strong>
                  );
                },
              },
            ]}
            summary={() => (
              <Table.Summary.Row style={{ background: '#fafafa' }}>
                <Table.Summary.Cell index={0} colSpan={3}>
                  <strong>Total Gasto / Servicio:</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <strong style={{ color: '#13c2c2' }}>
                    ${' '}
                    {(pendingValues?.inputs || [])
                      .reduce(
                        (sum: number, r: any) =>
                          sum +
                          (Number(r.quantity) || 0) *
                            (Number(r.unit_price) || 0),
                        0
                      )
                      .toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                  </strong>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            )}
          />
          <div style={{ marginTop: 8, fontSize: 12, color: '#13c2c2' }}>
            * Se registrará el gasto operativo en el ciclo y se generará el
            comprobante comercial/egreso automáticamente.
          </div>
        </div>
      )}

      {pendingValues?.log_type === 'OUTPUT_HARVEST' && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8, fontSize: 13 }}>
            <strong>Almacén de Destino: </strong>
            <Tag color="green">
              {storehouses.find((s: any) => s.id === pendingValues?.storehouse_id)
                ?.name ||
                storehouses.find(
                  (s: any) => s.id === pendingValues?.storehouse_id
                )?.alias ||
                'Bodega Principal'}
            </Tag>
          </div>
          <Table
            size="small"
            pagination={false}
            dataSource={pendingValues?.outputs || []}
            rowKey={(_, idx) => String(idx)}
            scroll={{ x: 'max-content' }}
            columns={[
              {
                title: 'Producto Cosechado / Producido',
                key: 'item',
                render: (_, record: any) => {
                  const item = allItems.find(
                    (i: any) => i.id === record.element_id
                  );
                  const code = item?.custom_id || item?.sku;
                  return (
                    <div>
                      <strong>{item?.name || 'Producto'}</strong>
                      {code && (
                        <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                          Ref: {code}
                        </div>
                      )}
                    </div>
                  );
                },
              },
              {
                title: 'Cantidad',
                key: 'quantity',
                align: 'right',
                render: (_, record: any) => {
                  const item = allItems.find(
                    (i: any) => i.id === record.element_id
                  );
                  return (
                    <span>
                      {record.quantity} {item?.measure_unit || ''}
                    </span>
                  );
                },
              },
              {
                title: 'Valor U. Est.',
                dataIndex: 'unit_price',
                key: 'unit_price',
                align: 'right',
                render: (val) =>
                  `$ ${Number(val || 0).toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`,
              },
              {
                title: 'Total Est.',
                key: 'total',
                align: 'right',
                render: (_, record: any) => {
                  const tot =
                    (Number(record.quantity) || 0) *
                    (Number(record.unit_price) || 0);
                  return (
                    <strong style={{ color: '#52c41a' }}>
                      ${' '}
                      {tot.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </strong>
                  );
                },
              },
            ]}
            summary={() => (
              <Table.Summary.Row style={{ background: '#fafafa' }}>
                <Table.Summary.Cell index={0} colSpan={3}>
                  <strong>Total Estimado Producción:</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <strong style={{ color: '#52c41a' }}>
                    ${' '}
                    {(pendingValues?.outputs || [])
                      .reduce(
                        (sum: number, r: any) =>
                          sum +
                          (Number(r.quantity) || 0) *
                            (Number(r.unit_price) || 0),
                        0
                      )
                      .toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                  </strong>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            )}
          />
          <div style={{ marginTop: 8, fontSize: 12, color: '#52c41a' }}>
            * Se <b>incrementará</b> el stock de los productos cosechados en el
            almacén de destino.
          </div>
        </div>
      )}

      {(pendingValues?.log_type === 'MORTALITY_LOSS' ||
        pendingValues?.log_type === 'UNIT_ADJUSTMENT') && (
        <div
          style={{
            background: '#fffbe6',
            padding: 12,
            borderRadius: 4,
            border: '1px solid #ffe58f',
            marginBottom: 12,
          }}
        >
          <strong>Ajuste de Capacidad: </strong>
          <span>
            {pendingValues?.quantity_change > 0
              ? `+${pendingValues.quantity_change}`
              : pendingValues.quantity_change}{' '}
            {cycle?.unit_of_measure || 'unidades'}
          </span>
        </div>
      )}

      <ul>
        {(pendingValues?.log_type === 'MORTALITY_LOSS' ||
          pendingValues?.log_type === 'UNIT_ADJUSTMENT' ||
          (pendingValues?.log_type === 'OUTPUT_HARVEST' &&
            cycle?.cycle_category === 'FINITE_TRANSFORMATION')) && (
          <li>Se actualizará la cantidad total del lote/ciclo.</li>
        )}
        <li>
          Esta acción generará movimientos de inventario que no se pueden
          deshacer automáticamente.
        </li>
      </ul>
    </Modal>
  );
};
